/**
 * The upgrades a customer ticks under the packages.
 *
 * Jonathon, 2026-08-26: "i also need to be able to add custom upgrades", and,
 * asked whether one should stick around, saved to the catalogue rather than
 * living on a single proposal.
 *
 * They were four rows set by a migration and reachable nowhere else — an
 * upgrade is a product with `proposal_upgrade_order` on it, and 437 products
 * behind a search box is not where anybody would find the four. So they get
 * their own short list.
 *
 * Two things this screen exists to say out loud:
 *
 *  - **An upgrade with no price is never offered.** The proposal drops one it
 *    cannot price rather than printing "$0.00", which is correct and
 *    invisible. `upgradeReadiness` turns that into a sentence on the row.
 *  - **Renaming one is how packages start charging for it.** The lists that
 *    say which packages already include an upgrade hold its *name*
 *    (migration 20260815130000), so a rename orphans them silently. Rule 8,
 *    one layer down: add a new one and retire the old.
 *
 * Taking an upgrade off the proposal clears `proposal_upgrade_order`. It never
 * deletes the product — the product may well be on a roof template, where it is
 * material somebody orders rather than an upgrade somebody ticks.
 */

import {
  formatCents,
  nextRowName,
  toCents,
  toDecimal,
  upgradeReadiness,
  upgradeRenameWarning,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t, tn } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { insertRow, updateRow, type UpgradeProduct } from "../../lib/queries";
import { supabase, unwrap } from "../../lib/supabase";
import { ToolInput } from "./SuppliersTool";

/** How far apart new rows sit, so one can be dropped between two later. */
const ORDER_STEP = 10;

export function UpgradesTool() {
  const [error, setError] = useState<string | null>(null);

  const query = useAsync(async () => {
    const [products, templates, estimates] = await Promise.all([
      supabase
        .from("products")
        .select(
          "id, name, description, default_price, measurement, coverage, waste, unit, active, proposal_upgrade_order",
        )
        .not("proposal_upgrade_order", "is", null)
        .order("proposal_upgrade_order"),
      supabase.from("roof_templates").select("name, included_upgrades"),
      supabase.from("estimates").select("included_upgrades"),
    ]);

    return {
      upgrades:
        unwrap<(UpgradeProduct & { active: boolean | null })[]>(products),
      templates:
        unwrap<{ name: string; included_upgrades: string[] | null }[]>(
          templates,
        ),
      estimateCount:
        unwrap<{ included_upgrades: string[] | null }[]>(estimates),
    };
  }, []);

  const upgrades = query.data?.upgrades ?? [];

  /** Which packages say they already include this upgrade. */
  const includedBy = (name: string): string[] =>
    (query.data?.templates ?? [])
      .filter((t) => (t.included_upgrades ?? []).includes(name))
      .map((t) => t.name);

  /** How many live estimates carry it as included, for the rename warning. */
  const estimatesIncluding = (name: string): number =>
    (query.data?.estimateCount ?? []).filter((e) =>
      (e.included_upgrades ?? []).includes(name),
    ).length;

  const guard = async (action: () => Promise<void>) => {
    try {
      await action();
      setError(null);
      await query.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const add = () =>
    void guard(async () => {
      const last = upgrades[upgrades.length - 1]?.proposal_upgrade_order ?? 0;
      await insertRow("products", {
        // Product names are unique (migration 20260731465000), and counting
        // the rows is not the same as counting past them: delete the middle
        // one of three and the count lands back on a name that exists.
        name: nextRowName(
          "New upgrade",
          upgrades.map((u) => u.name),
        ),
        category: "Upgrades",
        kind: "material",
        unit: "EA",
        active: true,
        // A flat price is the ordinary case for something typed here — a chase
        // cover, a skylight. Leave the measurement empty and it is offered once
        // at this price; fill it in and it is priced off the roof.
        default_price: 0,
        proposal_upgrade_order: Number(last) + ORDER_STEP,
      });
    });

  const rename = (upgrade: UpgradeProduct, next: string | null) => {
    const name = (next ?? "").trim();
    if (name === "" || name === upgrade.name) return;
    const blocked = upgradeRenameWarning(
      upgrade.name,
      name,
      includedBy(upgrade.name),
    );
    if (blocked) {
      window.alert(blocked);
      void query.reload();
      return;
    }
    const onEstimates = estimatesIncluding(upgrade.name);
    if (
      onEstimates > 0 &&
      !window.confirm(
        t(
          '{n} estimate(s) list "{name}" as already included. Renaming it means those estimates will charge for it instead. Rename anyway?',
          { n: onEstimates, name: upgrade.name },
        ),
      )
    ) {
      void query.reload();
      return;
    }
    void guard(() => updateRow("products", upgrade.id, { name }));
  };

  return (
    <>
      <ErrorNote error={error} />
      <div className="toolbar2">
        <span className="count">
          {tn(upgrades.length, "1 upgrade", "{n} upgrades")}
        </span>
        <button className="addrow" style={{ marginTop: 0 }} onClick={add}>
          {t("+ Add upgrade")}
        </button>
      </div>

      <p className="na-hint">
        {t(
          "These read under the packages on every proposal, in this order. Leave the measurement blank for a flat price; set one and the price is per unit, worked out from the roof.",
        )}
      </p>

      <div className="ewrap">
        <table className="etable">
          <thead>
            <tr>
              <th className="num">{t("Order")}</th>
              <th>{t("Name")}</th>
              <th>{t("What the customer reads")}</th>
              <th className="num">{t("Price")}</th>
              <th>{t("Measured against")}</th>
              <th className="num">{t("Covers")}</th>
              <th>{t("Offered")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {upgrades.length === 0 ? (
              <tr>
                <td colSpan={8} className="tnone">
                  {t("No upgrades. Add one and it appears on every proposal.")}
                </td>
              </tr>
            ) : (
              upgrades.map((upgrade) => {
                const why = upgradeReadiness(upgrade);
                const included = includedBy(upgrade.name);
                return (
                  <tr key={upgrade.id}>
                    <td className="num">
                      <ToolInput
                        value={String(upgrade.proposal_upgrade_order ?? "")}
                        width={50}
                        onSave={(v) =>
                          void guard(() =>
                            updateRow("products", upgrade.id, {
                              proposal_upgrade_order:
                                Number.parseInt(v ?? "", 10) || null,
                            }),
                          )
                        }
                      />
                    </td>
                    <td>
                      <ToolInput
                        value={upgrade.name}
                        width={220}
                        onSave={(v) => rename(upgrade, v)}
                      />
                      {included.length > 0 && (
                        <div className="na-hint">
                          {t("Included in {names}", {
                            names: included.join(", "),
                          })}
                        </div>
                      )}
                    </td>
                    <td>
                      <ToolInput
                        value={upgrade.description}
                        width={260}
                        onSave={(v) =>
                          void guard(() =>
                            updateRow("products", upgrade.id, {
                              description: v,
                            }),
                          )
                        }
                      />
                    </td>
                    <td className="num">
                      <ToolInput
                        value={
                          upgrade.default_price == null
                            ? ""
                            : String(upgrade.default_price)
                        }
                        width={80}
                        onSave={(v) =>
                          void guard(() =>
                            updateRow("products", upgrade.id, {
                              default_price: toDecimal(toCents(v)),
                            }),
                          )
                        }
                      />
                      <div className="na-hint">
                        {formatCents(toCents(upgrade.default_price))}
                        {upgrade.measurement
                          ? t(" per {unit}", {
                              unit: upgrade.unit ?? t("unit"),
                            })
                          : t(" flat")}
                      </div>
                    </td>
                    <td>
                      <ToolInput
                        value={upgrade.measurement}
                        width={180}
                        onSave={(v) =>
                          void guard(() =>
                            updateRow("products", upgrade.id, {
                              measurement: v,
                            }),
                          )
                        }
                      />
                    </td>
                    <td className="num">
                      <ToolInput
                        value={
                          upgrade.coverage == null
                            ? ""
                            : String(upgrade.coverage)
                        }
                        width={60}
                        onSave={(v) =>
                          void guard(() =>
                            updateRow("products", upgrade.id, {
                              coverage: Number.parseFloat(v ?? "") || null,
                            }),
                          )
                        }
                      />
                    </td>
                    <td className={why ? "na-hint" : undefined}>
                      {why ? t(why) : t("Yes")}
                    </td>
                    <td className="rowacts">
                      <button
                        className="btn-small"
                        title={t(
                          "Stop offering it on proposals. The product stays in the catalogue.",
                        )}
                        onClick={() =>
                          void guard(() =>
                            updateRow("products", upgrade.id, {
                              proposal_upgrade_order: null,
                            }),
                          )
                        }
                      >
                        {t("Take off")}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
